import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TextField, useTheme, useMediaQuery, Card, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import marcasTarjetasService, { MarcaTarjeta } from '../../services/marcasTarjetasService';

const MarcasTarjetas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [marcasData, setMarcasData] = useState<MarcaTarjeta[]>([]);
  const [filteredMarcas, setFilteredMarcas] = useState<MarcaTarjeta[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);

  const fetchMarcasTarjetas = async () => {
    try {
      setLoading(true);
      const data = await marcasTarjetasService.getMarcasTarjetas();
      setMarcasData(data || []);
      setFilteredMarcas(data || []);
    } catch (error: any) {
      console.error('Error al cargar tarjetas:', error);
      enqueueSnackbar('Error al cargar tarjetas', { variant: 'error' });
      setMarcasData([]);
      setFilteredMarcas([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMarcasTarjetas();
  }, []);

  useEffect(() => {
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) {
      setFilteredMarcas(marcasData);
    } else {
      const filtered = marcasData.filter(marca => 
        marca.codigoMarca?.toString().includes(term) ||
        marca.nombreMarca?.toLowerCase().includes(term) ||
        marca.numeroComercio?.toLowerCase().includes(term)
      );
      setFilteredMarcas(filtered);
    }
  }, [searchTerm, marcasData]);

  const handleDelete = async (codigoMarca: number) => {
    if (window.confirm('¿Está seguro de eliminar esta tarjeta?')) {
      try {
        await marcasTarjetasService.deleteMarcaTarjeta(codigoMarca);
        enqueueSnackbar('Tarjeta eliminada exitosamente', { variant: 'success' });
        fetchMarcasTarjetas();
      } catch (error: any) {
        console.error('Error al eliminar tarjeta:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar tarjeta', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Tarjetas
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tesoreria/tarjetas/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nueva Tarjeta
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código, nombre o número comercio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {filteredMarcas.map((marca) => (
                <Card 
                  key={marca.codigoMarca}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {marca.nombreMarca?.trim() || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Código: {marca.codigoMarca}
                          </Typography>
                          {marca.numeroComercio && (
                            <Typography variant="body2" color="text.secondary">
                              N° Comercio: {marca.numeroComercio}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Editar tarjeta">
                          <IconButton
                            onClick={() => navigate(`/tesoreria/tarjetas/${marca.codigoMarca}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar tarjeta">
                          <IconButton
                            onClick={() => handleDelete(marca.codigoMarca)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>N° Comercio</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>N° Cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMarcas.map((marca) => (
                    <TableRow 
                      key={marca.codigoMarca}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{marca.codigoMarca}</TableCell>
                      <TableCell>{marca.nombreMarca?.trim() || '-'}</TableCell>
                      <TableCell>{marca.numeroComercio || '-'}</TableCell>
                      <TableCell>{marca.nroCuenta || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar tarjeta">
                            <IconButton
                              onClick={() => navigate(`/tesoreria/tarjetas/${marca.codigoMarca}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar tarjeta">
                            <IconButton
                              onClick={() => handleDelete(marca.codigoMarca)}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MarcasTarjetas;

